/**
 * LOP 2 / 3 - VAN BAN DOM TRONG SUOT.
 *
 * Day la lop tra ve nhieu gia tri nhat so voi cong bo ra: dat cac the <span>
 * trong suot dung vi tri glyph, ta duoc MIEN PHI ca chon van ban bang chuot,
 * Ctrl+F cua trinh duyet, keo tha, va trinh doc man hinh. Khong the tai tao
 * duoc nhung thu do tren canvas voi bat ky chi phi hop ly nao.
 *
 * TOAN HOC O DAY LA CHO DE SAI NHAT CUA CA TRINH XEM:
 *
 *   - PDF co goc toa do o GOC DUOI BEN TRAI, truc y huong LEN.
 *     DOM co goc o GOC TREN BEN TRAI, truc y huong XUONG.
 *   - Moi muc van ban mang MA TRAN RIENG cua no (co chu, nghieng, xoay), va
 *     ma tran do KHONG bao gom phep chieu cua khung nhin.
 *
 * Nen phep bien doi dung la: tx = viewport.transform x item.transform.
 * Sau do (tx[4], tx[5]) la goc DUONG CO SO trong toa do DOM - phai tru di do
 * cao phan tren duong co so (ascent) moi ra duoc canh tren cua hop chu.
 *
 * Buoc cuoi: chieu rong glyph do font he thong ve KHONG khop chieu rong that
 * trong PDF. Ta do chieu rong thuc te roi ep bang scaleX(). Bo qua buoc nay
 * thi vung chon se lech dan ve cuoi moi dong.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { PDFPageProxy } from '../core/pdfjs';

export interface TextLayerProps {
  page: PDFPageProxy | null;
  zoom: number;
  /**
   * Do xoay HIEN THI (da cong phan xoay them cua so do trang).
   *
   * Phai la DUNG gia tri lop canvas dung. Lop van ban trong suot chi co gia tri
   * khi no nam CHINH XAC tren glyph; lech mot goc xoay thi vung chon, Ctrl+F va
   * trinh doc man hinh deu tro sang cho khac tren trang.
   */
  rotation: 0 | 90 | 180 | 270;
  /** Khong dung lop van ban khi dang cuon nhanh - no la phan dat nhat. */
  enabled: boolean;
}

interface PositionedItem {
  key: string;
  text: string;
  left: number;
  top: number;
  fontSize: number;
  fontFamily: string;
  angleRad: number;
  /** Chieu rong dich cua doan chu, don vi CSS pixel. */
  targetWidth: number;
}

type Matrix6 = [number, number, number, number, number, number];

/** m1 x m2. Cung quy uoc voi pdf.js (Util.transform). */
function mul(m1: readonly number[], m2: readonly number[]): Matrix6 {
  const a1 = m1[0] ?? 1, b1 = m1[1] ?? 0, c1 = m1[2] ?? 0;
  const d1 = m1[3] ?? 1, e1 = m1[4] ?? 0, f1 = m1[5] ?? 0;
  const a2 = m2[0] ?? 1, b2 = m2[1] ?? 0, c2 = m2[2] ?? 0;
  const d2 = m2[3] ?? 1, e2 = m2[4] ?? 0, f2 = m2[5] ?? 0;
  return [
    a1 * a2 + c1 * b2,
    b1 * a2 + d1 * b2,
    a1 * c2 + c1 * d2,
    b1 * c2 + d1 * d2,
    a1 * e2 + c1 * f2 + e1,
    b1 * e2 + d1 * f2 + f1,
  ];
}

interface RawItem {
  str: string;
  transform: number[];
  width: number;
  height: number;
  fontName: string;
}

function isTextItem(v: unknown): v is RawItem {
  return (
    typeof v === 'object' &&
    v !== null &&
    'str' in v &&
    Array.isArray((v as { transform?: unknown }).transform)
  );
}

export function TextLayer({ page, zoom, rotation, enabled }: TextLayerProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [items, setItems] = useState<PositionedItem[]>([]);
  const [ready, setReady] = useState(false);

  const pageNumber = page?.pageNumber ?? 0;

  useEffect(() => {
    if (!page || !enabled) {
      setItems([]);
      setReady(false);
      return;
    }
    let cancelled = false;
    setReady(false);

    void (async () => {
      try {
        const viewport = page.getViewport({ scale: zoom, rotation });
        const content = await page.getTextContent();
        if (cancelled) return;

        const styles = content.styles as Record<
          string,
          { fontFamily?: string; ascent?: number; descent?: number } | undefined
        >;
        const out: PositionedItem[] = [];
        let i = 0;

        for (const raw of content.items) {
          if (!isTextItem(raw)) continue;
          if (raw.str.length === 0) continue;

          const tx = mul(viewport.transform, raw.transform);
          const angleRad = Math.atan2(tx[1], tx[0]);
          // Chieu cao hop chu tren man hinh = do dai vector doc cua ma tran.
          const fontHeight = Math.hypot(tx[2], tx[3]);
          const style = styles[raw.fontName];
          const ascent = typeof style?.ascent === 'number' ? style.ascent : 0.8;
          const ascentPx = fontHeight * ascent;

          // (tx[4], tx[5]) la duong co so. Di len theo huong "len" cua chu -
          // voi chu xoay, huong do khong con la truc y cua man hinh nua.
          const left = tx[4] + ascentPx * Math.sin(angleRad);
          const top = tx[5] - ascentPx * Math.cos(angleRad);

          out.push({
            key: `t${i++}`,
            text: raw.str,
            left,
            top,
            fontSize: fontHeight,
            fontFamily: style?.fontFamily ?? 'sans-serif',
            angleRad,
            targetWidth: Math.abs(raw.width) * zoom,
          });
        }

        if (!cancelled) {
          setItems(out);
          setReady(true);
        }
      } catch (err) {
        if (!cancelled) console.warn('[textlayer] khong lay duoc noi dung van ban', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [page, zoom, rotation, enabled, pageNumber]);

  // Do va ep chieu rong. Chay sau khi DOM da co, TRUOC khi trinh duyet ve -
  // nguoi dung khong bao gio thay trang thai chua hieu chinh.
  useLayoutEffect(() => {
    if (!ready) return;
    const host = hostRef.current;
    if (!host) return;
    const spans = host.querySelectorAll<HTMLSpanElement>('span[data-w]');

    // XOA TRANSFORM CU TRUOC KHI DO.
    // React chi quan ly nhung thuoc tinh style co trong prop; transform o day
    // duoc dat bang tay nen React KHONG bao gio go no ra. Cac the <span> lai
    // dung khoa t0, t1, ... nen chung duoc TAI SU DUNG khi doi zoom hoac doi
    // trang. Neu do khi transform cu con nguyen thi getBoundingClientRect tra
    // ve chieu rong DA nhan he so cu, va he so moi = target / (actual * cu) ->
    // sai so nhan don sau moi lan doi zoom. Phai tra ve trang thai chua bien
    // dang truoc khi do.
    spans.forEach((span) => {
      span.style.transform = '';
    });

    // Doc TAT CA truoc, ghi TAT CA sau: tron doc/ghi se ep trinh duyet tinh
    // lai bo cuc mot lan cho moi the (layout thrashing).
    const scales: number[] = [];
    spans.forEach((span) => {
      const target = Number(span.dataset['w'] ?? '0');
      const actual = span.getBoundingClientRect().width;
      scales.push(target > 0 && actual > 0 ? target / actual : 1);
    });
    spans.forEach((span, idx) => {
      const scale = scales[idx] ?? 1;
      const angle = Number(span.dataset['a'] ?? '0');
      const rotate = angle === 0 ? '' : ` rotate(${angle}rad)`;
      span.style.transform = `${rotate} scaleX(${scale.toFixed(4)})`.trim();
    });
  }, [ready, items]);

  const rendered = useMemo(
    () =>
      items.map((it) => (
        <span
          key={it.key}
          data-w={it.targetWidth}
          data-a={it.angleRad}
          style={{
            left: `${it.left}px`,
            top: `${it.top}px`,
            fontSize: `${it.fontSize}px`,
            fontFamily: it.fontFamily,
          }}
        >
          {it.text}
        </span>
      )),
    [items],
  );

  return (
    <div className="textLayer" ref={hostRef} data-page={pageNumber}>
      {rendered}
    </div>
  );
}
